import { Button, Space, Typography } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import SuccessDocumentIcon from 'components/SuccessDocument';
import BaseTable from 'components/Table';
import { fetchAllCompanies } from 'api/companies';
import AddCompanyModal from './AddCompanyModal';
import { captureException } from 'utils/errors';
import { priceTransformer } from 'utils';
import dayjs from 'dayjs';

const columns = (navigate) => [
  {
    title: 'Company Name',
    dataIndex: 'name',
    key: 'name',
    width: '25%',
    render: (name, record) => (
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          navigate(`/console/customers/companies/${record.id}`);
        }}
      >
        {name || '-'}
      </a>
    ),
  },
  {
    title: 'Industry',
    dataIndex: 'industry',
    key: 'industry',
    width: '20%',
    render: (v) => v || '-',
  },
  {
    title: 'Contacts',
    dataIndex: 'contact_count',
    key: 'contact_count',
    width: '15%',
  },
  {
    title: 'Total Revenue',
    dataIndex: 'total_revenue',
    key: 'total_revenue',
    width: '20%',
    render: (value) => priceTransformer(value?.toFixed(2)),
  },
  {
    title: 'Last Order',
    dataIndex: 'last_order_date',
    key: 'last_order_date',
    width: '20%',
    render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-'),
  },
  {
    title: 'Status',
    dataIndex: 'is_active',
    key: 'is_active',
    width: '10%',
    render: (v) => (v ? 'Active' : 'Inactive'),
  },
];

function Companies({ search, resetFetchRef }) {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [dataVersion, setDataVersion] = useState(1);
  const navigate = useNavigate();

  const handleRefetch = useCallback(() => setDataVersion((v) => v + 1), []);

  const fetchCompanies = useCallback(
    (pagination, filter, sorter) => {
      const page = resetFetchRef?.current ? 1 : pagination.current;
      const queries = {
        search,
        page,
        page_size: pagination.pageSize,
      };

      if (resetFetchRef) resetFetchRef.current = false;

      return fetchAllCompanies(queries)
        .then((res) => {
          const data = res?.data;
          const inner = data?.data ?? data;
          const results = inner?.results ?? (Array.isArray(inner) ? inner : []);
          const count = inner?.count ?? 0;

          return {
            data: Array.isArray(results) ? results : [],
            total: count,
            page,
          };
        })
        .catch((e) => {
          captureException(e);
          throw e;
        });
    },
    [search, dataVersion, resetFetchRef],
  );

  const emptyComponent = useMemo(
    () => (
      <Space direction="vertical" className="my-10 mx-5" align="center">
        <SuccessDocumentIcon />
        <Typography.Paragraph>
          No companies yet. Add a company to get started.
        </Typography.Paragraph>
      </Space>
    ),
    [],
  );

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={() => setAddModalOpen(true)}>
          Add Company
        </Button>
      </div>
      <BaseTable
        id="companies"
        columns={columns(navigate)}
        fetchFn={fetchCompanies}
        empty={emptyComponent}
        scroll={{ x: 400 }}
      />
      <AddCompanyModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={() => {
          setAddModalOpen(false);
          handleRefetch();
        }}
      />
    </>
  );
}

export default Companies;
